import React, { useState, useEffect, useMemo } from 'react';
import { dataService } from '../services/dataService';
import { buildArticles } from '../utils/knowledgeBase';

const CATEGORIES = [
  { id: 'all',          label: 'All articles', icon: '≡'  },
  { id: 'overview',     label: 'Overview',     icon: '🏠' },
  { id: 'collections',  label: 'Data',         icon: '▦'  },
  { id: 'functions',    label: 'Functions',    icon: '⬡'  },
  { id: 'automations',  label: 'Automations',  icon: '⚡'  },
  { id: 'integrations', label: 'Integrations', icon: '⬢'  },
];

const SOURCE_LABELS = {
  human: { label: 'By human',       color: '#6366f1' },
  auto:  { label: 'Auto-generated', color: '#0ea5e9' },
  agent: { label: 'By agent',       color: '#10b981' },
};

// ── Rendering ─────────────────────────────────────────────────────────────────

function ArticleList({ articles, active, onSelect }) {
  return (
    <div className="kb-article-list">
      {articles.map(a => (
        <button key={a.id} className={`kb-article-item ${active === a.id ? 'active' : ''}`} onClick={() => onSelect(a.id)}>
          <div className="kb-article-item-title">{a.title}</div>
          <div className="kb-article-item-meta">
            <span className="kb-source-dot" style={{ background: SOURCE_LABELS[a.source]?.color }} />
            <span className="kb-article-item-date">{a.updatedAt}</span>
          </div>
        </button>
      ))}
    </div>
  );
}

function ArticleBody({ article, onBack }) {
  const src = SOURCE_LABELS[article.source];
  const lines = article.body.split('\n');
  return (
    <div className="kb-article-body">
      <div className="kb-article-header">
        {onBack && <button className="mobile-back-btn" onClick={onBack}>← Back</button>}
        <h2 className="kb-article-title">{article.title}</h2>
        <div className="kb-article-meta">
          <span className="kb-source-badge" style={{ background: src.color + '18', color: src.color }}>{src.label}</span>
          <span className="kb-meta-text">Updated by {article.updatedBy}</span>
          <span className="kb-meta-sep">·</span>
          <span className="kb-meta-text">{article.updatedAt}</span>
        </div>
      </div>

      <div className="kb-article-content">
        {lines.map((line, i) => {
          if (!line.trim()) return <div key={i} className="kb-line-gap" />;
          if (line.startsWith('**') && line.endsWith('**')) {
            return <p key={i} className="kb-bold-line">{line.replace(/\*\*/g, '')}</p>;
          }
          if (line.startsWith('- ')) {
            return <li key={i} className="kb-li">{line.slice(2).replace(/\*\*(.*?)\*\*/g, (_, t) => t)}</li>;
          }
          if (/^\d+\./.test(line)) {
            const [num, ...rest] = line.split('. ');
            return <li key={i} className="kb-li kb-li-num"><span className="kb-li-num-dot">{num}.</span>{rest.join('. ').replace(/\*\*(.*?)\*\*/g, (_, t) => t)}</li>;
          }
          return (
            <p key={i} className="kb-p">
              {line.split(/(\*\*.*?\*\*)/).map((chunk, j) => {
                if (chunk.startsWith('**') && chunk.endsWith('**')) return <strong key={j}>{chunk.slice(2, -2)}</strong>;
                return chunk;
              })}
            </p>
          );
        })}
      </div>
    </div>
  );
}

export default function KnowledgeBaseView({ workspaceId }) {
  const [pipeline, setPipeline] = useState({ nodes: [], edges: [] });
  const [category, setCategory] = useState('all');
  const [activeId, setActiveId] = useState(null);
  const [search, setSearch] = useState('');
  const [mobilePanel, setMobilePanel] = useState('cats');

  useEffect(() => {
    if (!workspaceId) return;
    const unsub = dataService.subscribePipeline(workspaceId, async (data) => {
      const pipeNodes = data.nodes || [];
      const colNodes = pipeNodes.filter(n => n.type === 'collectionNode');
      const counts = await Promise.all(colNodes.map(async (col) => {
        const count = await dataService.getRecordCount(workspaceId, col.id);
        return { id: col.id, count };
      }));
      const enriched = pipeNodes.map(n => {
        const c = counts.find(x => x.id === n.id);
        return c ? { ...n, _recordCount: c.count } : n;
      });
      setPipeline({ nodes: enriched, edges: data.edges || [] });
    });
    return () => unsub && unsub();
  }, [workspaceId]);

  const articles = useMemo(() => buildArticles(pipeline.nodes, pipeline.edges), [pipeline]);

  const filtered = articles.filter(a => {
    const inCat = category === 'all' || a.category === category;
    const inSearch = !search || a.title.toLowerCase().includes(search.toLowerCase()) || a.body.toLowerCase().includes(search.toLowerCase());
    return inCat && inSearch;
  });

  const activeArticle = articles.find(a => a.id === activeId) || filtered[0] || articles[0];

  const handleSearch = q => { setSearch(q); setCategory('all'); setMobilePanel('list'); };
  const handleCatClick = (id) => { setCategory(id); setSearch(''); setMobilePanel('list'); };
  const handleArticleClick = (id) => { setActiveId(id); setMobilePanel('content'); };
  const mc = (panel) => mobilePanel === panel ? 'mob-show' : 'mob-hide';

  return (
    <div className="kb-view">
      <div className={`kb-sidebar ${mc('cats')}`}>
        <div className="kb-search-wrap">
          <input className="kb-search" placeholder="Search knowledge base…" value={search} onChange={e => handleSearch(e.target.value)} />
        </div>

        <nav className="kb-categories">
          {CATEGORIES.map(c => (
            <button key={c.id} className={`kb-cat-item ${category === c.id && !search ? 'active' : ''}`} onClick={() => handleCatClick(c.id)}>
              <span className="kb-cat-icon">{c.icon}</span>
              {c.label}
              {c.id === 'all' && <span className="kb-cat-count">{articles.length}</span>}
            </button>
          ))}
        </nav>

        <div className="kb-sidebar-footer">
          <span className="kb-footer-note">
            Auto-generated from your<br />pipeline — updates as it changes
          </span>
        </div>
      </div>

      <div className={`kb-list-panel ${mc('list')}`}>
        <div className="kb-list-header">
          <button className="mobile-back-btn kb-list-back" onClick={() => setMobilePanel('cats')}>←</button>
          {search ? `Results for "${search}"` : CATEGORIES.find(c => c.id === category)?.label || 'All articles'}
          <span className="kb-list-count">{filtered.length}</span>
        </div>
        {filtered.length > 0
          ? <ArticleList articles={filtered} active={activeArticle?.id} onSelect={handleArticleClick} />
          : <div className="kb-empty">No articles found</div>}
      </div>

      <div className={`kb-content-panel ${mc('content')}`}>
        {activeArticle
          ? <ArticleBody article={activeArticle} onBack={() => setMobilePanel('list')} />
          : <div className="kb-empty">Select an article</div>}
      </div>
    </div>
  );
}
